import React, { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import makeupcreditService from '@/services/makeupCredit';
import { lessonService } from '@/services/lesson';
import { termService } from '@/services/term';
import type { Lesson, MakeupCredit, Term } from '@court-sync/shared';
import { toast } from 'react-hot-toast';
import { filterCurrentOrFutureTerms } from './termPickerUtils';

function toLocalDatetimeValue(d: Date | string): string {
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}T${pad(x.getHours())}:${pad(x.getMinutes())}`;
}

export interface EditManualCreditModalProps {
  isOpen: boolean;
  onClose: () => void;
  academyId: string;
  credit: MakeupCredit | null;
  onSaved: () => void;
}

export function EditManualCreditModal({
  isOpen,
  onClose,
  academyId,
  credit,
  onSaved,
}: EditManualCreditModalProps) {
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [allTerms, setAllTerms] = useState<Term[]>([]);
  const [lessonId, setLessonId] = useState('');
  const [termId, setTermId] = useState('');
  const [scope, setScope] = useState<'session' | 'term'>('session');
  const [reason, setReason] = useState('');
  const [expiresLocal, setExpiresLocal] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !academyId) {
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const [lessonRes, termRes] = await Promise.all([
          lessonService.getLessons({ academyId, activeStatus: 'active' }),
          termService.getTerms(academyId),
        ]);
        if (!cancelled && lessonRes.success && lessonRes.data) setLessons(lessonRes.data);
        if (!cancelled && termRes.success && termRes.data) {
          setAllTerms(termRes.data);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, academyId]);

  useEffect(() => {
    if (!isOpen || !credit) {
      return undefined;
    }
    setScope(credit.scope ?? 'session');
    setTermId(credit.termId ?? '');
    setReason(credit.manualReason ?? '');
    setExpiresLocal(toLocalDatetimeValue(credit.expiresAt));
    setLessonId('');
  }, [isOpen, credit]);

  const termsForSelect = useMemo(() => {
    const base = filterCurrentOrFutureTerms(allTerms);
    if (credit?.termId && !base.some((t) => t.id === credit.termId)) {
      const extra = allTerms.find((t) => t.id === credit.termId);
      if (extra) {
        return [...base, extra].sort((a, b) => {
          const sa = a.startDate instanceof Date ? a.startDate : new Date(a.startDate);
          const sb = b.startDate instanceof Date ? b.startDate : new Date(b.startDate);
          return sa.getTime() - sb.getTime();
        });
      }
    }
    return base;
  }, [allTerms, credit?.termId]);

  if (!isOpen || !credit) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (!expiresLocal.trim()) {
        toast.error('Please set an expiry date and time');
        return;
      }
      if (scope === 'term' && !termId) {
        toast.error('Select a term for a term-scoped credit');
        return;
      }
      const expiresAt = new Date(expiresLocal).toISOString();
      const body: Parameters<typeof makeupcreditService.updateManualCredit>[1] = {
        academyId,
        reason: reason.trim() || null,
        expiresAt,
      };
      if (scope === 'term') {
        body.scope = 'term';
        body.termId = termId;
      } else {
        body.scope = 'session';
      }
      if (lessonId === '__default') {
        body.lessonId = null;
      } else if (lessonId) {
        body.lessonId = lessonId;
      }
      await makeupcreditService.updateManualCredit(credit.id, body);
      onSaved();
      onClose();
    } catch (err: unknown) {
      console.error(err);
      const ax = err as { response?: { data?: { error?: string } }; message?: string };
      toast.error(ax.response?.data?.error || ax.message || 'Failed to update credit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-950"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Edit make-up credit</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Expires</label>
            <input
              type="datetime-local"
              value={expiresLocal}
              onChange={(e) => setExpiresLocal(e.target.value)}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">
              Lesson (optional — for matching rules)
            </label>
            <select
              value={lessonId}
              onChange={(e) => setLessonId(e.target.value)}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              disabled={loading}
            >
              <option value="">No change to lesson matching</option>
              <option value="__default">Reset to default (group) matching</option>
              {lessons.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              Optionally apply rules from a lesson, or reset matching to the default group profile.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Scope</label>
            <select
              value={scope}
              onChange={(e) => {
                const v = e.target.value as 'session' | 'term';
                setScope(v);
                if (v === 'session') setTermId('');
              }}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            >
              <option value="session">Session</option>
              <option value="term">Term (re-enrolment)</option>
            </select>
          </div>
          {scope === 'term' ? (
            <div>
              <label className="mb-1 block text-sm font-medium">Term</label>
              <select
                value={termId}
                onChange={(e) => setTermId(e.target.value)}
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                required
                disabled={loading}
              >
                <option value="">Select term…</option>
                {termsForSelect.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                Only current and future terms. Changing term updates expiry when you do not set a custom date above.
              </p>
            </div>
          ) : null}
          <div>
            <label className="mb-1 block text-sm font-medium">Reason (audit)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
